// Create / edit a warranty follow-on visit on a job. Single modal for both:
// pass a `visit` to edit (adds Complete/Reopen + Delete), or omit it to
// create. Reuses PeopleMultiSelect (people may be empty → unassigned).
// Visits are date-RANGED (start..end inclusive); the range drives the
// warranty-workdays rollup on the job. Past dates are blocked (warranty is
// forward-scheduled here) via the shared taskDateFloor guard.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, CheckCircle2, RotateCcw, ExternalLink } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { WarrantyVisitRow } from '@gutterinv/shared';
import {
  postWarrantyVisit,
  patchWarrantyVisit,
  deleteWarrantyVisit,
  setWarrantyVisitComplete,
} from '../api/client.js';
import { PeopleMultiSelect } from './PeopleMultiSelect.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { cn } from '../lib/utils.js';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog.js';
import { todayIso, taskDateFloor } from '../lib/format.js';

export function WarrantyVisitModal({
  visit,
  jobId,
  defaultDate,
  jobLabel,
  onClose,
}: {
  /** Provided → edit mode; omitted → create mode. */
  visit?: WarrantyVisitRow | null;
  /** Parent job — required for create + for cache invalidation in both modes. */
  jobId: string;
  /** Pre-fill date in create mode (e.g. a clicked calendar day). */
  defaultDate?: string;
  /** When set (calendar context), renders a "View job" link to /jobs/:id.
   *  Omitted on JobDetail where the job is already the current page. */
  jobLabel?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!visit;

  const [startDate, setStartDate] = useState<string>(
    visit?.visitDateStart ?? defaultDate ?? todayIso(),
  );
  const [endDate, setEndDate] = useState<string>(
    visit?.visitDateEnd ?? defaultDate ?? todayIso(),
  );
  const [personIds, setPersonIds] = useState<string[]>(
    visit ? visit.people.map((p) => p.id) : [],
  );
  const [notes, setNotes] = useState<string>(visit?.notes ?? '');

  const done = !!visit?.completedAt;

  // Past-date guard (block past; on edit keep an already-past start). Same
  // floor helper as the scheduled-task modal.
  const startFloor = taskDateFloor(isEdit, visit?.visitDateStart, todayIso());
  const startInvalid = startDate < startFloor;
  // End floors at the chosen start (a visit can't end before it begins).
  const endFloor = startDate > startFloor ? startDate : startFloor;
  const endInvalid = endDate < startDate;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['calendar', 'warranty'] });
    void queryClient.invalidateQueries({ queryKey: ['job', jobId, 'warranty'] });
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        visitDateStart: startDate,
        visitDateEnd: endDate,
        personIds,
        notes: notes.trim() || null,
      };
      return isEdit ? patchWarrantyVisit(visit!.id, payload) : postWarrantyVisit(jobId, payload);
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteWarrantyVisit(visit!.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => setWarrantyVisitComplete(visit!.id, !done),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const busy = saveMutation.isPending || deleteMutation.isPending || completeMutation.isPending;
  const saveDisabled = saveMutation.isPending || !startDate || !endDate || startInvalid || endInvalid;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[520px] max-w-[calc(100vw-2rem)] p-8 space-y-6" hideClose>
        <div className="flex items-center justify-between gap-3">
          <div>
            <DialogTitle className="text-base font-semibold text-foreground">
              {isEdit ? 'Edit warranty visit' : 'New warranty visit'}
            </DialogTitle>
            {jobLabel && (
              <Link
                to={`/jobs/${jobId}`}
                className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
                title="Open this job"
              >
                <ExternalLink className="h-3 w-3 shrink-0" />
                {jobLabel}
              </Link>
            )}
          </div>
          {isEdit && (
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => completeMutation.mutate()}
              title={done ? 'Reopen this visit' : 'Mark this visit done'}
            >
              {done ? <RotateCcw className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            </Button>
          )}
        </div>

        {done ? (
          /* Completed = frozen. Only the reopen button above is live. */
          <div className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
            Completed — locked. Use the reopen button above to edit.
          </div>
        ) : (
        <div className="space-y-4 rounded-md border border-border bg-secondary/30 p-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow mb-1 block">Start date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  const v = e.target.value;
                  setStartDate(v);
                  // keep end >= start as the user moves start forward
                  if (v && endDate < v) setEndDate(v);
                }}
                min={startFloor}
                aria-invalid={startInvalid}
                className={cn(
                  'h-8 text-xs',
                  startInvalid && 'border-destructive focus-visible:ring-destructive',
                )}
                disabled={busy}
              />
            </div>
            <div>
              <label className="eyebrow mb-1 block">End date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={endFloor}
                aria-invalid={endInvalid}
                className={cn(
                  'h-8 text-xs',
                  endInvalid && 'border-destructive focus-visible:ring-destructive',
                )}
                disabled={busy}
              />
            </div>
          </div>
          {(startInvalid || endInvalid) && (
            <p className="text-[0.6875rem] text-destructive">
              {startInvalid ? "Start date can't be in the past." : 'End date must be on or after the start date.'}
            </p>
          )}
          <div>
            <label className="eyebrow mb-1 block">People</label>
            <PeopleMultiSelect value={personIds} onChange={setPersonIds} disabled={busy} />
            <p className="mt-1 text-[0.6875rem] text-muted-foreground">
              Leave empty to park the visit unassigned.
            </p>
          </div>
          <div>
            <label className="eyebrow mb-1 block">Notes (optional)</label>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. what needs repair…"
              className="h-8 text-xs"
              disabled={busy}
              maxLength={2000}
            />
          </div>
          {saveMutation.error && (
            <div className="text-xs text-destructive">
              {saveMutation.error instanceof Error ? saveMutation.error.message : 'Save failed'}
            </div>
          )}
        </div>
        )}

        <div className="flex gap-2">
          {!done && (
            <Button className="flex-1" disabled={saveDisabled} onClick={() => saveMutation.mutate()}>
              {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save' : 'Schedule visit'}
            </Button>
          )}
          {isEdit && !done && (
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => deleteMutation.mutate()}
              title="Delete visit"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
